import React, { useState, useRef, useEffect } from 'react';
import { MessageSquare, X, Send, Bot, Sparkles } from 'lucide-react';
import api from '../services/api';

const quickSuggestions = [
  'Summary',
  'Sales this month',
  'Cash balance',
  'Receivables',
  'Low stock',
  'Top ledgers',
  'Vehicle expiries',
];

// Minimal markdown to HTML for bot responses (bold + line breaks)
function renderBotMessage(text) {
  const escaped = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  const html = escaped
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\n/g, '<br/>');
  return { __html: html };
}

export default function Chatbot() {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([
    { role: 'bot', text: "👋 Hi! I'm your ERP assistant. Ask me about sales, cash, receivables, stock, or type **help** to see what I can do." }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const bodyRef = useRef(null);

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [msgs, loading, open]);

  const send = async (text) => {
    const q = (text || input).trim();
    if (!q || loading) return;
    setMsgs(m => [...m, { role: 'user', text: q }]);
    setInput('');
    setLoading(true);
    try {
      const { data } = await api.post('/chatbot', { message: q });
      setMsgs(m => [...m, { role: 'bot', text: data.answer || 'Sorry, I couldn\'t process that.' }]);
    } catch (e) {
      setMsgs(m => [...m, { role: 'bot', text: '⚠ Unable to reach server. Please try again.' }]);
    } finally { setLoading(false); }
  };

  const onKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      {!open && (
        <button className="chat-fab pulse" onClick={() => setOpen(true)} aria-label="Open chat assistant">
          <MessageSquare size={24} />
        </button>
      )}

      {open && (
        <div className="chat-window">
          <div className="chat-header">
            <div style={{display:'flex',alignItems:'center',gap:10,minWidth:0}}>
              <div style={{width:36,height:36,borderRadius:'50%',background:'rgba(255,255,255,0.2)',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                <Bot size={18}/>
              </div>
              <div style={{minWidth:0}}>
                <h4 style={{display:'flex',alignItems:'center',gap:5}}>ERP Assistant <Sparkles size={12}/></h4>
                <div className="sub">Ask about your business data</div>
              </div>
            </div>
            <button className="chat-close" onClick={() => setOpen(false)} aria-label="Close chat"><X size={16}/></button>
          </div>

          <div className="chat-body" ref={bodyRef}>
            {msgs.map((m, i) => (
              <div key={i} className={'chat-msg ' + m.role}>
                {m.role === 'bot' ? (
                  <span dangerouslySetInnerHTML={renderBotMessage(m.text)} />
                ) : (
                  m.text
                )}
              </div>
            ))}
            {loading && (
              <div className="chat-msg bot" style={{padding:0}}>
                <div className="chat-typing"><span/><span/><span/></div>
              </div>
            )}
          </div>

          <div className="chat-quick">
            {quickSuggestions.map(s => (
              <button key={s} onClick={() => send(s)} disabled={loading}>{s}</button>
            ))}
          </div>

          <div className="chat-input">
            <input
              type="text"
              placeholder="Ask anything about your business..."
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={onKey}
              disabled={loading}
            />
            <button onClick={() => send()} disabled={!input.trim() || loading} aria-label="Send">
              <Send size={16} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
